import { useState } from "react";
import axios from "axios";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { FlaskConical, Play, RotateCcw, RefreshCw, AlertTriangle, CheckCircle2, Wrench } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DemoPanel = () => {
  const token = localStorage.getItem("sa_token");
  const headers = { Authorization: `Bearer ${token}` };
  const [loading, setLoading] = useState(null);
  const [confermaReset, setConfermaReset] = useState(false);

  const run = async (action) => {
    setLoading(action);
    const endpoint = { setup: "setup", update: "update", reset: "reset" }[action];
    try {
      const res = await axios.post(`${API}/admin/demo/${endpoint}`, null, { headers, timeout: 30000 });
      const msg = res.data?.message || "Operazione avviata";
      toast.success(msg, { duration: 8000 });
      if (action === "reset") setConfermaReset(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || `Errore durante ${action}`);
    } finally {
      setLoading(null);
    }
  };

  const fixDemoWebhook = async () => {
    setLoading("fix_webhook");
    try {
      const res = await axios.post(`${API}/telegram/fix-demo-webhook`, null, { headers });
      toast.success(res.data?.message || "Webhook demo corretto!", { duration: 8000 });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore correzione webhook");
    } finally {
      setLoading(null);
    }
  };

  const Step = ({ n, text }) => (
    <div className="flex items-start gap-3">
      <div className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">{n}</div>
      <p className="text-sm text-gray-600">{text}</p>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Dialog conferma reset */}
      <Dialog open={confermaReset} onOpenChange={setConfermaReset}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />Reset completo demo
            </DialogTitle>
            <DialogDescription>
              Cancella <strong>tutti i dati</strong> dell'account demo e li rigenera da zero.
              L'operazione richiede 2-3 minuti.
            </DialogDescription>
          </DialogHeader>
          <div className="bg-red-50 rounded-xl p-3 text-sm text-red-700 border border-red-100">
            Tutti i dati storici (cassa, presenze, ordini, ecc.) verranno cancellati e ricreati.
          </div>
          <DialogFooter className="flex gap-2 mt-2">
            <Button variant="outline" className="flex-1" onClick={() => setConfermaReset(false)}>Annulla</Button>
            <Button className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              onClick={() => run("reset")} disabled={!!loading} data-testid="btn-conferma-reset-demo">
              {loading === "reset"
                ? <><RefreshCw className="h-4 w-4 mr-1.5 animate-spin" />In corso...</>
                : <><RotateCcw className="h-4 w-4 mr-1.5" />Sì, resetta</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <FlaskConical className="h-5 w-5 text-violet-600" />Account Demo
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Gestione dell'account demo per i venditori — La Trattoria Romana + Il Bistrot Milanese
          </p>
        </div>
      </div>

      {/* Credenziali */}
      <div className="bg-violet-50 rounded-2xl p-5 border border-violet-100 space-y-3">
        <p className="text-sm font-semibold text-violet-800 mb-3">Credenziali account demo</p>
        {[
          ["Email Admin", "demo@yollo.me"],
          ["Email Operatore", "operatore@yollo.me"],
          ["Password (entrambi)", "demo2026"],
          ["Bot Telegram", "@YolloDemoBot"],
          ["Password bot", "yollo@2026"],
        ].map(([label, val]) => (
          <div key={label} className="flex items-center justify-between">
            <span className="text-xs text-violet-600 w-28">{label}</span>
            <code className="text-sm font-mono bg-white border border-violet-200 rounded-lg px-3 py-1 text-violet-900">{val}</code>
          </div>
        ))}
      </div>

      {/* Azioni */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {/* Setup statico */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 space-y-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-indigo-100 flex items-center justify-center">
              <Play className="h-4 w-4 text-indigo-600" />
            </div>
            <div>
              <p className="font-semibold text-sm text-gray-900">Setup Iniziale</p>
              <p className="text-xs text-gray-400">Dati statici (una volta)</p>
            </div>
          </div>
          <p className="text-xs text-gray-500">
            Crea società, locali, utenti, ricette, fornitori, ingredienti, documenti, frigo, task pulizia.
          </p>
          <Button size="sm" className="w-full rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white"
            onClick={() => run("setup")} disabled={!!loading} data-testid="btn-demo-setup">
            {loading === "setup"
              ? <><RefreshCw className="h-3.5 w-3.5 mr-1.5 animate-spin" />In corso...</>
              : <><Play className="h-3.5 w-3.5 mr-1.5" />Esegui Setup</>}
          </Button>
        </div>

        {/* Aggiorna dati */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 space-y-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-emerald-100 flex items-center justify-center">
              <RefreshCw className="h-4 w-4 text-emerald-600" />
            </div>
            <div>
              <p className="font-semibold text-sm text-gray-900">Aggiorna Dati</p>
              <p className="text-xs text-gray-400">Aggiunge dati mancanti</p>
            </div>
          </div>
          <p className="text-xs text-gray-500">
            Aggiunge cassa, presenze, turni, temperature, ordini del giorno corrente (idempotente).
          </p>
          <Button size="sm" className="w-full rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white"
            onClick={() => run("update")} disabled={!!loading} data-testid="btn-demo-update">
            {loading === "update"
              ? <><RefreshCw className="h-3.5 w-3.5 mr-1.5 animate-spin" />In corso...</>
              : <><RefreshCw className="h-3.5 w-3.5 mr-1.5" />Aggiorna</>}
          </Button>
        </div>

        {/* Reset completo */}
        <div className="bg-white rounded-2xl border border-red-100 shadow-sm p-5 space-y-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-red-100 flex items-center justify-center">
              <RotateCcw className="h-4 w-4 text-red-600" />
            </div>
            <div>
              <p className="font-semibold text-sm text-gray-900">Reset Completo</p>
              <p className="text-xs text-gray-400">Cancella e rigenera tutto</p>
            </div>
          </div>
          <p className="text-xs text-gray-500">
            Rimuove tutti i dati demo e li rigenera da zero. Usare prima di demo importanti.
          </p>
          <Button size="sm" variant="outline"
            className="w-full rounded-xl text-red-600 hover:bg-red-50 border-red-200"
            onClick={() => setConfermaReset(true)} disabled={!!loading} data-testid="btn-demo-reset">
            {loading === "reset"
              ? <><RefreshCw className="h-3.5 w-3.5 mr-1.5 animate-spin" />In corso...</>
              : <><RotateCcw className="h-3.5 w-3.5 mr-1.5" />Reset Demo</>}
          </Button>
        </div>
      </div>

      {/* Come funziona */}
      <div className="bg-gray-50 rounded-2xl p-5 border border-gray-100">
        <p className="text-sm font-semibold text-gray-700 mb-4">Come funziona la demo</p>
        <div className="space-y-3">
          <Step n="1" text="Premi 'Esegui Setup' una sola volta per creare tutta la struttura (dipendenti, ricette, fornitori, documenti, ecc.)" />
          <Step n="2" text="Premi 'Aggiorna Dati' per aggiungere i dati del giorno (cassa, presenze, turni). Si esegue anche automaticamente ogni notte alle 00:05." />
          <Step n="3" text="Prima di una demo importante, premi 'Reset Completo' per avere dati freschi e puliti da gennaio 2026 ad oggi." />
          <Step n="4" text="I venditori aprono @YolloDemoBot su Telegram, inseriscono la password 'yollo@2026' e possono fare tutte le domande." />
        </div>
      </div>

      {/* Scheduler automatico */}
      <div className="flex items-start gap-3 bg-emerald-50 rounded-2xl p-4 border border-emerald-100">
        <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0 mt-0.5" />
        <div className="text-sm text-emerald-700">
          <p className="font-semibold">Aggiornamento automatico attivo</p>
          <p className="text-emerald-600 mt-0.5">
            Ogni notte alle 00:05 il sistema aggiunge automaticamente i dati mancanti del giorno (turni, presenze, cassa, temperature, ordini).
          </p>
        </div>
      </div>

      {/* Fix webhook demo */}
      <div className="flex items-start gap-3 bg-amber-50 rounded-2xl p-4 border border-amber-100">
        <Wrench className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm font-semibold text-amber-800">Problema bot demo risponde tramite altro bot?</p>
          <p className="text-xs text-amber-700 mt-0.5 mb-3">
            Se @YolloDemoBot usa per errore il webhook di un altro bot (es. risposte errate, errori trascrizione vocale),
            clicca qui per correggere il webhook e ricollegarlo a /webhook-demo.
          </p>
          <Button size="sm" variant="outline"
            className="rounded-xl text-amber-700 border-amber-300 hover:bg-amber-100"
            onClick={fixDemoWebhook}
            disabled={!!loading}
            data-testid="btn-fix-demo-webhook"
          >
            {loading === "fix_webhook"
              ? <><RefreshCw className="h-3.5 w-3.5 mr-1.5 animate-spin" />Correzione in corso...</>
              : <><Wrench className="h-3.5 w-3.5 mr-1.5" />Correggi Webhook Demo</>}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DemoPanel;
